import { Component, OnInit, Input, ViewChildren, QueryList, HostListener} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IUProperty } from '../lib/public_api';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { IButtonData } from '../../shared/components/bg-button/bg-button.component';
import { ILicensePanelMappedItem } from './components/card-item/card-item.component';
import { CardItemComponent } from './components/card-item/card-item.component';
import { MqService } from 'src/app/service/mq.service';

interface ILicensePanelItem {
  information: IUProperty<string>;
}

interface ILicensePanelData {
  headerMini: IUProperty<string>;
  subHeaderMini: IUProperty<string>;
  priceMini: IUProperty<string>;
  descriptionMini: IUProperty<string>;
  termsMini: IUProperty<string>;
  linkMini: IUProperty<IButtonData>
  linkColorMini: IUProperty<string>;
  itemsMini: IUProperty<ILicensePanelItem[]>;

  headerBasic: IUProperty<string>;
  subHeaderBasic: IUProperty<string>;
  priceBasic: IUProperty<string>;
  descriptionBasic: IUProperty<string>;
  termsBasic: IUProperty<string>;
  linkBasic: IUProperty<IButtonData>
  linkColorBasic: IUProperty<string>;
  itemsBasic: IUProperty<ILicensePanelItem[]>;

  headerElite: IUProperty<string>;
  subHeaderElite: IUProperty<string>;
  priceElite: IUProperty<string>;
  descriptionElite: IUProperty<string>;
  termsElite: IUProperty<string>;
  linkElite: IUProperty<IButtonData>
  linkColorElite: IUProperty<string>;
  itemsElite: IUProperty<ILicensePanelItem[]>;

  headerCorp: IUProperty<string>;
  subHeaderCorp: IUProperty<string>;
  priceCorp: IUProperty<string>;
  descriptionCorp: IUProperty<string>;
  termsCorp: IUProperty<string>;
  linkCorp: IUProperty<IButtonData>
  linkColorCorp: IUProperty<string>;
  itemsCorp: IUProperty<ILicensePanelItem[]>;
}

enum InfoState {
  opened = 'opened',
  closed = 'closed'
}

@Component({
  selector: 'app-license-panel',
  templateUrl: './license-panel.component.html',
  styleUrls: ['./license-panel.component.less']
})

export class LicensePanelComponent implements OnInit {
  @Input() data: ILicensePanelData;
  mappedData: ILicensePanelMappedItem[] = [];

  @ViewChildren('cardItem') cardItems: QueryList<CardItemComponent>;

  @HostListener('window:resize', ['$event']) onResize(event?) {
    this.runCardsAdjustments();
  }

  constructor(
    private _sanitizer: DomSanitizer,
    private mq: MqService,
  ) { }

  ngOnInit() {
    this.mapData();
  }

  ngAfterViewInit(): void {
    this.runCardsAdjustments();
  }

  runCardsAdjustments() {
    this.mq.mobileDesktop(
      () => {
        this.cleanCardsHeigth(this.cardItems.toArray())
      }, 
      () => {
        if (this.mq.isTabletAndLaptop()) {
          this.adjustCardsHeight(this.cardItems.toArray().slice(0, 2));
          this.adjustCardsHeight(this.cardItems.toArray().slice(2));
        } else {
          this.adjustCardsHeight(this.cardItems.toArray());
        }
      })
  }

  adjustCardsHeight(cardItems: CardItemComponent[]) {
    let maxContentHeight = 0;
    let maxDescriptionHeight = 0;

    this.cleanCardsHeigth(cardItems);
    
    cardItems.forEach(((card: CardItemComponent) => {
      const contentSize = card.contentRef && card.contentRef.nativeElement && card.contentRef.nativeElement.getClientRects();
      const contentHeight = contentSize && contentSize[0].height;
      const descriptionSize = card.descriptionRef && card.descriptionRef.nativeElement && card.descriptionRef.nativeElement.getClientRects();
      const descriptionHeight = descriptionSize && descriptionSize[0].height;

      if (contentHeight > maxContentHeight) {
        maxContentHeight = contentHeight;
      }
      
      if (descriptionHeight > maxDescriptionHeight) {
        maxDescriptionHeight = descriptionHeight;
      }
    }))

    this.setCardsHeight(cardItems, maxContentHeight, maxDescriptionHeight)
  }

  cleanCardsHeigth(cards: CardItemComponent[]) {
    cards.forEach((card: CardItemComponent) => {
      if (card.contentRef && card.contentRef.nativeElement) {
        card.contentRef.nativeElement.style.height = '';
      }
      if (card.descriptionRef && card.descriptionRef.nativeElement) {
        card.descriptionRef.nativeElement.style.height = '';
      }
    })
  }

  setCardsHeight(cards: CardItemComponent[], maxContentHeight, maxDescriptionHeight) {
    cards.forEach((card: CardItemComponent) => {
      if (card.contentRef && card.contentRef.nativeElement && maxContentHeight) {
        card.contentRef.nativeElement.style.height = maxContentHeight + 'px';
      }
      if (card.descriptionRef && card.descriptionRef.nativeElement && maxDescriptionHeight) {
        card.descriptionRef.nativeElement.style.height = maxDescriptionHeight + 'px';
      }
    })
  }

  assembleHTMLItem(item: string) {
    return this._sanitizer.bypassSecurityTrustHtml(item);
  }

  mapData() {
    //Map into 4 same blocks of data, so that we can use reusable component
    this.mappedData = [
      {
        header: this.data && this.data.headerMini && this.data.headerMini.value,
        subHeader: this.data && this.data.subHeaderMini && this.data.subHeaderMini.value,
        price: this.data && this.data.priceMini && this.data.priceMini.value,
        terms: this.data && this.data.termsMini && this.data.termsMini.value,
        description: this.data && this.data.descriptionMini && this.data.descriptionMini.value && this.assembleHTMLItem(this.data.descriptionMini.value),
        link: this.data && this.data.linkMini && this.data.linkMini.value,
        linkColor: this.data && this.data.linkColorMini && this.data.linkColorMini.value,
        informationArray: this.data && this.data.itemsMini && this.data.itemsMini.value && this.data.itemsMini.value.map((item: ILicensePanelItem) => ({
          information: item.information && item.information.value && this.assembleHTMLItem(item.information.value)
        })),
        modifierForCssClass: 'mini'
      },
      {
        header: this.data && this.data.headerBasic && this.data.headerBasic.value,
        subHeader: this.data && this.data.subHeaderBasic && this.data.subHeaderBasic.value,
        price: this.data && this.data.priceBasic && this.data.priceBasic.value,
        terms: this.data && this.data.termsBasic && this.data.termsBasic.value,
        description: this.data && this.data.descriptionBasic && this.data.descriptionBasic.value && this.assembleHTMLItem(this.data.descriptionBasic.value),
        link: this.data && this.data.linkBasic && this.data.linkBasic.value,
        linkColor: this.data && this.data.linkColorBasic && this.data.linkColorBasic.value,
        informationArray: this.data && this.data.itemsBasic && this.data.itemsBasic.value && this.data.itemsBasic.value.map((item: ILicensePanelItem) => ({
          information: item.information && item.information.value && this.assembleHTMLItem(item.information.value)
        })),
        modifierForCssClass: 'basic'
      },
      {
        header: this.data && this.data.headerElite && this.data.headerElite.value,
        subHeader: this.data && this.data.subHeaderElite && this.data.subHeaderElite.value,
        price: this.data && this.data.priceElite && this.data.priceElite.value,
        terms: this.data && this.data.termsElite && this.data.termsElite.value,
        description: this.data && this.data.descriptionElite && this.data.descriptionElite.value && this.assembleHTMLItem(this.data.descriptionElite.value),
        link: this.data && this.data.linkElite && this.data.linkElite.value,
        linkColor: this.data && this.data.linkColorElite && this.data.linkColorElite.value,
        informationArray: this.data && this.data.itemsElite && this.data.itemsElite.value && this.data.itemsElite.value.map((item: ILicensePanelItem) => ({
          information: item.information && item.information.value && this.assembleHTMLItem(item.information.value)
        })),
        modifierForCssClass: 'elite'
      },
      {
        header: this.data && this.data.headerCorp && this.data.headerCorp.value,
        subHeader: this.data && this.data.subHeaderCorp && this.data.subHeaderCorp.value,
        price: this.data && this.data.priceCorp && this.data.priceCorp.value,
        terms: this.data && this.data.termsCorp && this.data.termsCorp.value,
        description: this.data && this.data.descriptionCorp && this.data.descriptionCorp.value && this.assembleHTMLItem(this.data.descriptionCorp.value),
        link: this.data && this.data.linkCorp && this.data.linkCorp.value,
        linkColor: this.data && this.data.linkColorCorp && this.data.linkColorCorp.value,
        informationArray: this.data && this.data.itemsCorp && this.data.itemsCorp.value && this.data.itemsCorp.value.map((item: ILicensePanelItem) => ({
          information: item.information && item.information.value && this.assembleHTMLItem(item.information.value)
        })),
        modifierForCssClass: 'corp'
      },
    ]
  }
}
